// Performance monitoring and Core Web Vitals tracking

// Core Web Vitals thresholds
const VITALS_THRESHOLDS = {
  LCP: 2500, // Largest Contentful Paint (ms)
  FID: 100,  // First Input Delay (ms)
  CLS: 0.1,  // Cumulative Layout Shift
  FCP: 1800, // First Contentful Paint (ms)
  TTFB: 800, // Time to First Byte (ms)
};

// Função para medir Core Web Vitals
export const measureWebVitals = () => {
  if (typeof window === 'undefined') return;

  // Largest Contentful Paint (LCP)
  if ('PerformanceObserver' in window) {
    const lcpObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1];
      const lcp = lastEntry.startTime;
      
      if (lcp <= VITALS_THRESHOLDS.LCP) {
        console.log('✅ LCP is good:', lcp + 'ms');
      } else {
        console.warn('⚠️ LCP needs improvement:', lcp + 'ms');
      }
      
      // Enviar para analytics se disponível
      if (window.gtag) {
        window.gtag('event', 'web_vitals', {
          event_category: 'Performance',
          event_label: 'LCP',
          value: Math.round(lcp),
        });
      }
    });
    
    try {
      lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });
    } catch (e) {
      console.warn('LCP observer not supported');
    }
  }

  // First Input Delay (FID)
  if ('PerformanceObserver' in window) {
    const fidObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach((entry) => {
        const fid = entry.processingStart - entry.startTime;
        
        if (fid <= VITALS_THRESHOLDS.FID) {
          console.log('✅ FID is good:', fid + 'ms');
        } else {
          console.warn('⚠️ FID needs improvement:', fid + 'ms');
        }
        
        // Enviar para analytics se disponível
        if (window.gtag) {
          window.gtag('event', 'web_vitals', {
            event_category: 'Performance',
            event_label: 'FID',
            value: Math.round(fid),
          });
        }
      });
    });
    
    try {
      fidObserver.observe({ entryTypes: ['first-input'] });
    } catch (e) {
      console.warn('FID observer not supported');
    }
  }

  // Cumulative Layout Shift (CLS)
  if ('PerformanceObserver' in window) {
    let clsValue = 0;
    const clsObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach((entry) => {
        if (!entry.hadRecentInput) {
          clsValue += entry.value;
        }
      });
      
      if (clsValue <= VITALS_THRESHOLDS.CLS) {
        console.log('✅ CLS is good:', clsValue);
      } else {
        console.warn('⚠️ CLS needs improvement:', clsValue);
      }
      
      // Enviar para analytics se disponível
      if (window.gtag) {
        window.gtag('event', 'web_vitals', {
          event_category: 'Performance',
          event_label: 'CLS',
          value: Math.round(clsValue * 1000), // Convert to integer
        });
      }
    });
    
    try {
      clsObserver.observe({ entryTypes: ['layout-shift'] });
    } catch (e) {
      console.warn('CLS observer not supported');
    }
  }
};

// Função para medir tempo de carregamento da página
export const measurePageLoadTime = () => {
  if (typeof window === 'undefined') return;

  window.addEventListener('load', () => {
    const navigation = performance.getEntriesByType('navigation')[0];
    const loadTime = navigation.loadEventEnd - navigation.loadEventStart;
    const domContentLoaded = navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart;
    
    console.log('Page Load Time:', loadTime + 'ms');
    console.log('DOM Content Loaded:', domContentLoaded + 'ms');
    
    // Enviar para analytics se disponível
    if (window.gtag) {
      window.gtag('event', 'page_load_time', {
        event_category: 'Performance',
        event_label: 'Load Time',
        value: Math.round(loadTime),
      });
    }
  });
};

// Função para medir tempo de carregamento de imagens
export const measureImageLoadTime = (imageSelector) => {
  if (typeof window === 'undefined') return;

  const images = document.querySelectorAll(imageSelector);
  images.forEach((img, index) => {
    const startTime = performance.now();
    
    img.addEventListener('load', () => {
      const loadTime = performance.now() - startTime;
      console.log(`Image ${index + 1} load time:`, loadTime + 'ms');
      
      // Enviar para analytics se disponível
      if (window.gtag) {
        window.gtag('event', 'image_load_time', {
          event_category: 'Performance',
          event_label: `Image ${index + 1}`,
          value: Math.round(loadTime),
        });
      }
    });
  });
};

// Função para otimizar imagens lazy loading
export const initLazyLoading = () => {
  if (typeof window === 'undefined') return;

  const images = document.querySelectorAll('img[data-src]');
  
  const imageObserver = new IntersectionObserver((entries, observer) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        const img = entry.target;
        img.src = img.dataset.src;
        img.classList.remove('lazy');
        observer.unobserve(img);
      }
    });
  });

  images.forEach((img) => {
    imageObserver.observe(img);
  });
};

// Função para pré-carregar recursos críticos
export const preloadCriticalResources = () => {
  if (typeof window === 'undefined') return;

  const criticalResources = [
    '/fonts/primary-font.woff2',
    '/css/critical.css',
    '/images/hero-image.jpg',
  ];

  criticalResources.forEach((resource) => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.href = resource;
    link.as = resource.endsWith('.css') ? 'style' : 'image';
    document.head.appendChild(link);
  });
};

// Função para otimizar fontes
export const optimizeFonts = () => {
  if (typeof window === 'undefined') return;

  // Adicionar display=swap para melhor performance
  const fontLinks = document.querySelectorAll('link[href*="fonts.googleapis.com"]');
  fontLinks.forEach((link) => {
    if (!link.href.includes('display=')) {
      link.href += '&display=swap';
    }
  });
};

// Função para medir tempo de interação
export const measureTimeToInteractive = () => {
  if (typeof window === 'undefined') return;

  const startTime = performance.now();
  
  const measureTTI = () => {
    const tti = performance.now() - startTime;
    console.log('Time to Interactive:', tti + 'ms');
    
    // Enviar para analytics se disponível
    if (window.gtag) {
      window.gtag('event', 'time_to_interactive', {
        event_category: 'Performance',
        event_label: 'TTI',
        value: Math.round(tti),
      });
    }
  };

  // Medir TTI quando a página estiver totalmente interativa
  if (document.readyState === 'complete') {
    measureTTI();
  } else {
    window.addEventListener('load', measureTTI);
  }
};

// Função para inicializar todas as medições de performance
export const initPerformanceMonitoring = () => {
  measureWebVitals();
  measurePageLoadTime();
  measureTimeToInteractive();
  initLazyLoading();
  preloadCriticalResources();
  optimizeFonts();
};

// Função para reportar erros de performance
export const reportPerformanceError = (error) => {
  console.error('Performance Error:', error);
  
  if (window.gtag) {
    window.gtag('event', 'performance_error', {
      event_category: 'Performance',
      event_label: error.message || 'Unknown error',
      value: 1,
    });
  }
};
